// src/pages/BatchPayments.jsx  v3
// Changes vs v2:
//   1. Fast page load — AbortController + 8 s timeout, instant empty state
//   2. Professional WalletDropdown — custom select with avatar + balance chip
//   3. Rich RecipientCard — per-method fields: bank name, account #, routing,
//      account type, SWIFT, IBAN, crypto address, network; expandable card layout

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePlaidLink } from 'react-plaid-link';
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Upload, Plus, Trash2, Play, X, CheckCircle, AlertCircle, ChevronDown,
  Download, RefreshCw, FileText, Users, Calendar, DollarSign, ChevronUp,
  TrendingDown, Loader, CreditCard, Building, Bitcoin, Wallet, Lock, Check,
  Globe, Hash, ArrowRight
} from 'lucide-react';

// ─── Stripe ────────────────────────────────────────────────────
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : Promise.resolve(null);

// ─── API ────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com';

// ─── Shared input classes ────────────────────────────────────────
const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 text-sm';
const sel = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm';
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

// ─── Status badge ───────────────────────────────────────────────
const STATUS = {
  draft:      { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Draft' },
  pending:    { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Scheduled' },
  processing: { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Processing' },
  completed:  { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Completed' },
  failed:     { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Failed' },
  cancelled:  { bg: 'bg-gray-100',   text: 'text-gray-500',   label: 'Cancelled' },
};
function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.draft;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
}

// ─── Currency colours & initials ────────────────────────────────
const COIN_META = {
  BTC:  { bg: 'bg-orange-500', label: '₿' },
  ETH:  { bg: 'bg-indigo-500', label: 'Ξ' },
  USDT: { bg: 'bg-green-500',  label: '₮' },
  USD:  { bg: 'bg-blue-500',   label: '$' },
  EUR:  { bg: 'bg-blue-600',   label: '€' },
  GBP:  { bg: 'bg-purple-600', label: '£' },
};
function CoinAvatar({ currency, size = 9 }) {
  const m = COIN_META[currency] || { bg: 'bg-gray-500', label: currency?.charAt(0) || '?' };
  const sz = `w-${size} h-${size}`;
  return (
    <div className={`${sz} ${m.bg} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ fontSize: size <= 8 ? 11 : 14 }}>
      {m.label}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WALLET DROPDOWN  — professional custom select
// ─────────────────────────────────────────────────────────────────
function WalletDropdown({ wallets, cards, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const all = [...wallets, ...cards];

  // Close on outside click
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedItem = all.find(w => w.id === selected);

  function balance(item) {
    const b = item.btc_balance ?? item.current_btc_balance ?? 0;
    const usd = item.usd_balance ?? item.current_usd_value ?? null;
    return { crypto: parseFloat(b).toFixed(8), usd };
  }

  function label(item) {
    return item.name || item.wallet_name || `Card ****${item.card_number?.slice(-4) || 'XXXX'}`;
  }
  function currency(item) {
    return item.currency || item.coin || 'BTC';
  }

  if (all.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center text-gray-400 text-sm">
        <Wallet size={28} className="mx-auto mb-2 opacity-30" />
        No wallets found — create one from the Wallets tab.
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 bg-white transition-all text-left
          ${open ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300'}`}>
        {selectedItem ? (
          <>
            <CoinAvatar currency={currency(selectedItem)} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm truncate">{label(selectedItem)}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {balance(selectedItem).crypto} {currency(selectedItem)}
                {balance(selectedItem).usd !== null && (
                  <span className="ml-1.5 text-gray-400">≈ ${parseFloat(balance(selectedItem).usd).toFixed(2)} USD</span>
                )}
              </div>
            </div>
            <div className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200 whitespace-nowrap">
              {balance(selectedItem).crypto} {currency(selectedItem)}
            </div>
          </>
        ) : (
          <div className="flex-1 text-gray-400 text-sm">Select wallet…</div>
        )}
        <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500 font-medium px-2">Your wallets & cards</p>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
            {all.map(item => {
              const isActive = item.id === selected;
              const bal = balance(item);
              return (
                <button key={item.id} type="button"
                  onClick={() => { onSelect(item.id); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                    ${isActive ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <CoinAvatar currency={currency(item)} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-sm truncate ${isActive ? 'text-purple-700' : 'text-gray-900'}`}>
                      {label(item)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {bal.crypto} {currency(item)}
                      {bal.usd !== null && <span className="ml-1.5 text-gray-400">≈ ${parseFloat(bal.usd).toFixed(2)} USD</span>}
                    </div>
                  </div>
                  {isActive
                    ? <Check size={16} className="text-purple-600 flex-shrink-0" />
                    : <div className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{currency(item)}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// RECIPIENT CARD — expandable card with full payment fields
// ─────────────────────────────────────────────────────────────────
const METHODS = [
  { value: 'wallet',           label: 'Zanors Wallet', icon: <Wallet size={14} className="text-purple-500" /> },
  { value: 'bank_domestic',    label: 'Bank (Domestic)', icon: <Building size={14} className="text-blue-500" /> },
  { value: 'bank_international', label: 'Bank (International)', icon: <Globe size={14} className="text-indigo-500" /> },
  { value: 'crypto',           label: 'Crypto / Blockchain', icon: <Bitcoin size={14} className="text-orange-500" /> },
];

const CRYPTO_NETWORKS = ['Bitcoin (BTC)', 'Ethereum (ETH)', 'Tron (USDT-TRC20)', 'Solana (SOL)', 'BNB Chain (BEP-20)', 'Polygon (MATIC)', 'Ripple (XRP)', 'Litecoin (LTC)', 'Other'];
const ACCT_TYPES      = ['Checking', 'Savings', 'Business Checking', 'Business Savings'];
const CURRENCIES      = ['USD','EUR','GBP','NGN','KES','GHS','ZAR','BRL','CAD','AUD','JPY','CHF','AED','SAR'];

function RecipientCard({ recipient: r, index, onChange: upd, onRemove }) {
  const [expanded, setExpanded] = useState(index === 0); // first row open by default
  const method = r.payment_method || 'wallet';
  const cell = `${inp} mt-1`;

  function field(name, label, placeholder, type = 'text', opts) {
    if (opts?.type === 'select') {
      return (
        <div>
          <label className={lbl}>{label}</label>
          <select className={`${sel} mt-1`} value={r[name] || ''} onChange={e => upd(index, name, e.target.value)}>
            <option value="">Select…</option>
            {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    return (
      <div>
        <label className={lbl}>{label}</label>
        <input className={cell} placeholder={placeholder} type={type}
          value={r[name] || ''} onChange={e => upd(index, name, e.target.value)} />
      </div>
    );
  }

  const isComplete = r.recipient_name && r.amount;
  const methodMeta = METHODS.find(m => m.value === method) || METHODS[0];

  return (
    <div className={`border-2 rounded-xl transition-all mb-3 ${expanded ? 'border-purple-200 shadow-sm' : 'border-gray-200'}`}>
      {/* ── Collapsed header (always visible) ── */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(o => !o)}>
        {/* Row number */}
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
          ${isComplete ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
          {isComplete ? <Check size={13} /> : index + 1}
        </div>

        {/* Summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm truncate">
              {r.recipient_name || <span className="text-gray-400 font-normal">Recipient {index + 1}</span>}
            </span>
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium
              ${method === 'wallet' ? 'bg-purple-100 text-purple-700' :
                method.startsWith('bank') ? 'bg-blue-100 text-blue-700' :
                'bg-orange-100 text-orange-700'}`}>
              {methodMeta.icon} {methodMeta.label}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
            {r.recipient_email && <span>{r.recipient_email}</span>}
            {r.amount && (
              <span className="font-semibold text-gray-700">
                {parseFloat(r.amount).toFixed(2)} {r.currency || 'USD'}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button type="button" onClick={e => { e.stopPropagation(); onRemove(index); }}
            className="text-red-400 hover:text-red-600 p-1 transition-colors">
            <Trash2 size={15} />
          </button>
          {expanded
            ? <ChevronUp size={16} className="text-gray-400" />
            : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {/* ── Expanded fields ── */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-4">

          {/* Row 1: Name + Email + Amount + Currency */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {field('recipient_name', 'Full Name *', 'John Doe')}
            {field('recipient_email', 'Email Address', 'john@example.com', 'email')}
            {field('amount', 'Amount *', '0.00', 'number')}
            <div>
              <label className={lbl}>Currency</label>
              <select className={`${sel} mt-1`} value={r.currency || 'USD'}
                onChange={e => upd(index, 'currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Payment Method */}
          <div>
            <label className={lbl}>Payment Method</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
              {METHODS.map(m => (
                <button key={m.value} type="button"
                  onClick={() => upd(index, 'payment_method', m.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                    ${method === m.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                  {m.icon} <span className="truncate">{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Method-specific fields ── */}

          {/* WALLET */}
          {method === 'wallet' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
              {field('zanors_email', 'Zanors Account Email', 'recipient@zanors.com', 'email')}
              {field('notes', 'Payment Notes', 'e.g. Salary for March')}
            </div>
          )}

          {/* BANK — DOMESTIC */}
          {method === 'bank_domestic' && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {field('bank_name', 'Bank Name *', 'e.g. Chase Bank')}
                {field('account_holder', 'Account Holder Name', 'Legal name on account')}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {field('account_number', 'Account Number *', 'e.g. 123456789')}
                {field('routing_number', 'Routing Number *', 'e.g. 021000021')}
                {field('account_type', 'Account Type', '', 'text', { type: 'select', options: ACCT_TYPES })}
              </div>
              {field('notes', 'Payment Reference / Memo', 'e.g. Invoice #1042')}
            </div>
          )}

          {/* BANK — INTERNATIONAL */}
          {method === 'bank_international' && (
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {field('bank_name', 'Bank Name *', 'e.g. Barclays')}
                {field('account_holder', 'Account Holder Name', 'Legal name on account')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('iban', 'IBAN *', 'e.g. GB29NWBK60161331926819')}
                {field('swift_bic', 'SWIFT / BIC Code *', 'e.g. NWBKGB2L')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('bank_country', 'Bank Country', 'e.g. United Kingdom')}
                {field('bank_city', 'Bank City', 'e.g. London')}
              </div>
              {field('notes', 'Payment Reference / Purpose', 'e.g. Contractor payment Q1')}
            </div>
          )}

          {/* CRYPTO */}
          {method === 'crypto' && (
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {field('crypto_network', 'Network / Coin *', '', 'text', { type: 'select', options: CRYPTO_NETWORKS })}
                {field('crypto_amount_currency', 'Pay In', '', 'text', { type: 'select', options: ['USD equivalent', 'BTC', 'ETH', 'USDT', 'SOL', 'XRP'] })}
              </div>
              {field('crypto_address', 'Wallet Address *', 'e.g. 1A2B3C4D5E6F...')}
              {field('notes', 'Memo / Tag (if required)', 'e.g. Destination tag for XRP')}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ─── CSV template download ──────────────────────────────────────
function downloadTemplate() {
  const rows = [
    ['name','email','amount','currency','payment_method','bank_name','account_holder','account_number','routing_number','account_type','iban','swift_bic','crypto_network','crypto_address','zanors_email','notes'],
    ['John Doe','john@example.com','500.00','USD','wallet','','','','','','','','','','john@zanors.com','March salary'],
    ['Jane Smith','jane@example.com','1200.00','USD','bank_domestic','Chase Bank','Jane Smith','123456789','021000021','Checking','','','','','','Invoice #112'],
    ['Acme Ltd','acme@company.io','3000.00','EUR','bank_international','Barclays','Acme Ltd','','','','GB29NWBK60161331926819','NWBKGB2L','','','','Q1 services'],
    ['Bob Wilson','bob@example.com','0.05','BTC','crypto','','','','','','','','Bitcoin (BTC)','1A2B3C4D5E6F7','','Freelance'],
  ];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' }));
  a.download = 'zanors_batch_template.csv';
  a.click();
}

// ══════════════════════════════════════════════════════════════════
// CREATE BATCH MODAL
// ══════════════════════════════════════════════════════════════════
function CreateBatchModal({ onClose, onCreated, user }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [step,       setStep]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [csvParsing, setCsvParsing] = useState(false);
  const fileInputRef = useRef();

  const [wallets,      setWallets]      = useState([]);
  const [cards,        setCards]        = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);

  const [selectedFunding,     setSelectedFunding]     = useState('wallet');
  const [selectedWallet,      setSelectedWallet]      = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  const [linkToken,    setLinkToken]    = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);

  const [form, setForm] = useState({
    title: '', description: '', currency: 'USD',
    payment_type: 'one_time', source_wallet: 'USD', scheduled_at: '',
  });
  const [recipients, setRecipients] = useState([
    { recipient_name:'', recipient_email:'', amount:'', currency:'USD', payment_method:'wallet' }
  ]);
  const [recurrence, setRecurrence] = useState({
    frequency:'monthly', day_of_month:1, day_of_week:1,
    start_date: new Date().toISOString().split('T')[0], end_date:'', max_runs:''
  });

  const totalAmount = recipients.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const feeSaved    = (totalAmount*0.03).toFixed(2);

  // Load wallets (lazy — only when modal opens)
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([loadWalletsAndCards(), loadBankAccounts()])
      .finally(() => setWalletsLoading(false));
  }, [user]);

  const loadWalletsAndCards = async () => {
    const [{ data: w }, { data: c }] = await Promise.all([
      supabase.from('wallets').select('*').eq('user_id', user.id).order('created_at', { ascending:true }),
      supabase.from('crypto_cards').select('*').eq('activated_by', user.id).eq('status','activated').order('activated_at',{ascending:false})
    ]);
    setWallets(w || []);
    setCards(c || []);
    if (w?.length > 0) setSelectedWallet(w[0].id);
  };

  const loadBankAccounts = async () => {
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API}/payments/bank-accounts`, { headers:{ Authorization:`Bearer ${session.access_token}` } });
      if (res.ok) { const d = await res.json(); setBankAccounts(d.data?.bankAccounts || []); }
    } catch {}
  };

  // Plaid — exact same as SendMoney.jsx
  const createLinkToken = async () => {
    setPlaidLoading(true);
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${API}/payments/plaid/create-link-token`, {
        method:'POST', headers:{ Authorization:`Bearer ${session.access_token}`, 'Content-Type':'application/json' }
      });
      if (!res.ok) throw new Error('Failed to create link token');
      const d = await res.json();
      setLinkToken(d.data.linkToken);
    } catch (err) { setError('Failed to initialise bank connection'); }
    finally { setPlaidLoading(false); }
  };

  const onPlaidSuccess = async (publicToken) => {
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${API}/payments/plaid/exchange-token`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${session.access_token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ publicToken })
      });
      if (!res.ok) throw new Error('Failed to link bank account');
      await loadBankAccounts();
    } catch (err) { setError('Failed to link bank account'); }
  };

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({ token:linkToken, onSuccess:onPlaidSuccess });

  const addRow    = ()          => setRecipients(p=>[...p,{recipient_name:'',recipient_email:'',amount:'',currency:form.currency,payment_method:'wallet'}]);
  const removeRow = i           => setRecipients(p=>p.filter((_,idx)=>idx!==i));
  const updateRow = (i,f,val)   => setRecipients(p=>p.map((r,idx)=>idx===i?{...r,[f]:val}:r));

  async function handleCSV(e) {
    const file = e.target.files[0]; if (!file) return;
    setCsvParsing(true); setError('');
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/batch-payments/parse-csv`, {
        method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}` }, body:fd
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setRecipients(d.recipients);
    } catch (err) { setError(err.message); }
    finally { setCsvParsing(false); e.target.value=''; }
  }

  async function handleSubmit() {
    setLoading(true); setError('');
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let stripePaymentMethodId = null;
      if (selectedFunding === 'card') {
        if (!stripe || !elements) throw new Error('Stripe not loaded yet');
        const { error: se, paymentMethod } = await stripe.createPaymentMethod({ type:'card', card:elements.getElement(CardElement) });
        if (se) throw new Error(se.message);
        stripePaymentMethodId = paymentMethod.id;
      }
      if (selectedFunding === 'bank' && !selectedBankAccount)
        throw new Error('Please select a linked bank account');

      const payload = {
        ...form, funding_method: selectedFunding,
        ...(selectedFunding==='wallet'||selectedFunding==='crypto' ? { selected_wallet_id:selectedWallet } : {}),
        ...(selectedFunding==='bank' ? { bank_account_id:selectedBankAccount } : {}),
        ...(selectedFunding==='card' ? { stripe_payment_method_id:stripePaymentMethodId } : {}),
        recipients,
        ...(form.payment_type==='recurring' ? {
          recurrence_rule:{ ...recurrence, max_runs:recurrence.max_runs?parseInt(recurrence.max_runs):null }
        } : {})
      };

      const res = await fetch(`${API}/batch-payments`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${session.access_token}` },
        body: JSON.stringify(payload)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error||'Failed to create batch');
      onCreated(d.batch);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const canContinue =
    step===1 ? !!form.title.trim() :
    step===2 ? recipients.some(r=>r.recipient_name&&r.amount) : true;

  const submitDisabled =
    loading
    || (selectedFunding==='card'   && (!stripe||!elements))
    || (selectedFunding==='bank'   && !selectedBankAccount)
    || (selectedFunding==='wallet' && !selectedWallet && wallets.length===0 && cards.length===0);

  const STEPS = ['Details','Recipients','Schedule','Review'];
  const allWallets = [...wallets, ...cards];

  // Funding source tab config
  const FUNDING_TABS = [
    { id:'wallet', icon:<Wallet size={18}/>, label:'Zanors Wallet',     color:'purple' },
    { id:'bank',   icon:<Building size={18}/>, label:'Bank Account',    color:'green'  },
    { id:'card',   icon:<CreditCard size={18}/>, label:'Debit/Credit Card', color:'blue' },
    { id:'crypto', icon:<Bitcoin size={18}/>, label:'Cryptocurrency',   color:'orange' },
  ];
  const TAB_ACTIVE = {
    purple:'border-purple-500 text-purple-700 bg-purple-50',
    green: 'border-green-500  text-green-700  bg-green-50',
    blue:  'border-blue-500   text-blue-700   bg-blue-50',
    orange:'border-orange-500 text-orange-700 bg-orange-50',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Batch Payment</h2>
            <p className="text-sm text-gray-500 mt-0.5">Pay multiple recipients in one transaction</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={22} />
          </button>
        </div>

        {/* Step pills */}
        <div className="flex px-6 pt-4 gap-2 border-b border-gray-100 pb-3">
          {STEPS.map((s,i) => (
            <button key={s} type="button"
              onClick={()=>i<step?setStep(i+1):null}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                ${step===i+1?'bg-purple-600 text-white shadow-sm'
                :step>i+1?'bg-green-100 text-green-700 cursor-pointer'
                :'bg-gray-100 text-gray-400 cursor-default'}`}>
              {step>i+1&&<CheckCircle size={12}/>}{s}
            </button>
          ))}
          <div className="ml-auto text-xs text-gray-400 self-center">Step {step} of 4</div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <AlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5"/>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* ══ STEP 1 — Details & Funding ══ */}
          {step===1 && (
            <div className="space-y-6 max-w-xl">

              <div>
                <label className={lbl}>Batch Name *</label>
                <input className={`${inp} mt-1`} placeholder="e.g. March Payroll, Q1 Contractor Payments"
                  value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} />
              </div>

              <div>
                <label className={lbl}>Description <span className="normal-case text-gray-400 font-normal">(optional)</span></label>
                <textarea className={`${inp} mt-1`} rows={2} placeholder="Notes about this batch…"
                  value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Base Currency</label>
                  <select className={`${sel} mt-1`} value={form.currency} onChange={e=>setForm(p=>({...p,currency:e.target.value}))}>
                    {['USD','EUR','GBP','BTC','ETH','USDT','NGN','KES','GHS','ZAR','BRL','CAD'].map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Payment Type</label>
                  <select className={`${sel} mt-1`} value={form.payment_type} onChange={e=>setForm(p=>({...p,payment_type:e.target.value}))}>
                    <option value="one_time">One-Time</option>
                    <option value="recurring">Recurring</option>
                  </select>
                </div>
              </div>

              {/* ── FUNDING SOURCE ── */}
              <div>
                <label className={lbl}>Funding Source</label>
                <p className="text-xs text-gray-500 mb-3">How you will fund this batch payment</p>

                {/* Tab strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {FUNDING_TABS.map(t => (
                    <button key={t.id} type="button"
                      onClick={() => {
                        setSelectedFunding(t.id);
                        if (t.id==='bank' && !linkToken) createLinkToken();
                      }}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 text-xs font-semibold transition-all
                        ${selectedFunding===t.id ? TAB_ACTIVE[t.color] : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'}`}>
                      {t.icon}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ── Wallet dropdown panel ── */}
                {selectedFunding==='wallet' && (
                  <div className="space-y-3">
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <p className="text-xs font-semibold text-purple-700 mb-2.5">Select wallet to deduct from</p>
                      {walletsLoading
                        ? <div className="flex items-center gap-2 text-sm text-gray-400"><Loader size={15} className="animate-spin"/> Loading wallets…</div>
                        : <WalletDropdown wallets={wallets} cards={cards} selected={selectedWallet} onSelect={setSelectedWallet} />
                      }
                    </div>
                    {selectedWallet && (() => {
                      const w = allWallets.find(x=>x.id===selectedWallet);
                      const usd = w?.usd_balance ?? w?.current_usd_value ?? null;
                      return w && usd !== null ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          Available balance:
                          <strong className="text-gray-900">${parseFloat(usd).toFixed(2)} USD</strong>
                          <span className="text-gray-400">({parseFloat(w.btc_balance||w.current_btc_balance||0).toFixed(8)} {w.currency||'BTC'})</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* ── Bank (Plaid) panel ── */}
                {selectedFunding==='bank' && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-100 space-y-2">
                    <p className="text-xs font-semibold text-green-700 mb-2">Select a linked bank account</p>
                    {bankAccounts.length===0 ? (
                      <button type="button"
                        onClick={()=>plaidReady&&openPlaid()}
                        disabled={!plaidReady||plaidLoading}
                        className="w-full p-4 border-2 border-dashed border-green-400/60 rounded-xl text-green-600 flex items-center justify-center gap-2 text-sm font-semibold hover:border-green-500 transition-colors disabled:opacity-50">
                        {plaidLoading
                          ? <><Loader size={17} className="animate-spin"/>Preparing Plaid…</>
                          : <><Building size={17}/>Connect Bank Account via Plaid</>}
                      </button>
                    ) : (
                      <>
                        {bankAccounts.map(acct=>(
                          <button key={acct.id} type="button"
                            onClick={()=>setSelectedBankAccount(acct.id)}
                            className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left
                              ${selectedBankAccount===acct.id?'border-green-500 bg-white':'border-gray-200 bg-white hover:border-green-300'}`}>
                            <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Building size={17} className="text-green-600"/>
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 text-sm">{acct.bank_name}</div>
                              <div className="text-xs text-gray-500">Account ****{acct.account_number_last4}</div>
                            </div>
                            {selectedBankAccount===acct.id&&<Check size={17} className="text-green-600"/>}
                          </button>
                        ))}
                        <button type="button" onClick={()=>plaidReady&&openPlaid()} disabled={!plaidReady}
                          className="w-full py-2.5 border border-dashed border-green-400 rounded-lg text-xs text-green-600 hover:bg-green-50 transition-colors font-semibold">
                          + Add Another Bank Account
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* ── Stripe card panel ── */}
                {selectedFunding==='card' && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs font-semibold text-blue-700 mb-3">Enter card details</p>
                    <div className="bg-white rounded-xl border border-blue-200 p-4 shadow-sm">
                      <CardElement options={{
                        style:{
                          base:{ fontSize:'15px', color:'#1f2937', fontFamily:'system-ui,-apple-system,sans-serif','::placeholder':{color:'#9ca3af'} },
                          invalid:{ color:'#dc2626' }, complete:{ color:'#059669' }
                        }
                      }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                      <Lock size={11} className="text-green-500"/>
                      Secured by Stripe — card details never stored on our servers
                    </p>
                  </div>
                )}

                {/* ── Crypto panel ── */}
                {selectedFunding==='crypto' && (
                  <div className="space-y-3">
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <p className="text-xs font-semibold text-orange-700 mb-2.5">Select crypto wallet to deduct from</p>
                      {walletsLoading
                        ? <div className="flex items-center gap-2 text-sm text-gray-400"><Loader size={15} className="animate-spin"/>Loading wallets…</div>
                        : <WalletDropdown wallets={wallets} cards={cards} selected={selectedWallet} onSelect={setSelectedWallet}/>
                      }
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* ══ STEP 2 — Recipients ══ */}
          {step===2 && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-gray-800">{recipients.length} recipient{recipients.length!==1?'s':''}</p>
                  <p className="text-sm text-gray-500">
                    Total: <strong className="text-gray-900">{totalAmount.toFixed(2)} {form.currency}</strong>
                    {'  '}<span className="text-green-600 text-xs font-medium">— saves ~${feeSaved} vs wire transfers</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={downloadTemplate}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                    <Download size={13}/> CSV Template
                  </button>
                  <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCSV}/>
                  <button onClick={()=>fileInputRef.current?.click()} disabled={csvParsing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {csvParsing?<Loader size={13} className="animate-spin"/>:<Upload size={13}/>}
                    Upload CSV
                  </button>
                  <button onClick={addRow}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                    <Plus size={13}/> Add Recipient
                  </button>
                </div>
              </div>

              {recipients.length===0 ? (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
                  <Users size={44} className="mx-auto mb-3 text-gray-300"/>
                  <p className="text-gray-500 font-medium">No recipients yet</p>
                  <p className="text-gray-400 text-sm mt-1">Click "Add Recipient" or upload a CSV file</p>
                </div>
              ) : (
                <div>
                  {recipients.map((r,i)=>(
                    <RecipientCard key={i} recipient={r} index={i} onChange={updateRow} onRemove={removeRow}/>
                  ))}
                </div>
              )}

              {/* Summary bar */}
              {recipients.length > 0 && (
                <div className="mt-4 flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users size={15}/> <span><strong className="text-gray-900">{recipients.length}</strong> recipients</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign size={15}/> <span>Total: <strong className="text-gray-900">{totalAmount.toFixed(2)} {form.currency}</strong></span>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 text-green-600 font-semibold text-xs">
                    <CheckCircle size={14}/> ~${feeSaved} saved vs wire transfers
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ STEP 3 — Schedule ══ */}
          {step===3 && (
            <div className="max-w-lg space-y-5">
              {form.payment_type==='one_time' ? (
                <div>
                  <label className={lbl}>Process Date & Time</label>
                  <p className="text-xs text-gray-500 mb-2">Leave empty to process immediately after review</p>
                  <input type="datetime-local" className={`${inp} mt-1`}
                    value={form.scheduled_at} min={new Date().toISOString().slice(0,16)}
                    onChange={e=>setForm(p=>({...p,scheduled_at:e.target.value}))}/>
                </div>
              ) : (
                <>
                  <div>
                    <label className={lbl}>Frequency</label>
                    <select className={`${sel} mt-1`} value={recurrence.frequency} onChange={e=>setRecurrence(p=>({...p,frequency:e.target.value}))}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  {recurrence.frequency==='weekly'&&(
                    <div>
                      <label className={lbl}>Day of Week</label>
                      <select className={`${sel} mt-1`} value={recurrence.day_of_week} onChange={e=>setRecurrence(p=>({...p,day_of_week:parseInt(e.target.value)}))}>
                        {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i)=><option key={d} value={i}>{d}</option>)}
                      </select>
                    </div>
                  )}
                  {['monthly','quarterly'].includes(recurrence.frequency)&&(
                    <div>
                      <label className={lbl}>Day of Month</label>
                      <input type="number" min={1} max={28} className={`${inp} mt-1`} value={recurrence.day_of_month}
                        onChange={e=>setRecurrence(p=>({...p,day_of_month:parseInt(e.target.value)}))}/>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>Start Date *</label>
                      <input type="date" className={`${inp} mt-1`} value={recurrence.start_date}
                        onChange={e=>setRecurrence(p=>({...p,start_date:e.target.value}))}/>
                    </div>
                    <div>
                      <label className={lbl}>End Date (optional)</label>
                      <input type="date" className={`${inp} mt-1`} value={recurrence.end_date}
                        onChange={e=>setRecurrence(p=>({...p,end_date:e.target.value}))}/>
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Max Runs (empty = unlimited)</label>
                    <input type="number" min={1} className={`${inp} mt-1`} placeholder="e.g. 12"
                      value={recurrence.max_runs} onChange={e=>setRecurrence(p=>({...p,max_runs:e.target.value}))}/>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ STEP 4 — Review ══ */}
          {step===4 && (
            <div className="space-y-5">
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-100">
                <h3 className="font-bold text-gray-900 text-lg mb-3">{form.title}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label:'Recipients',   value:recipients.length,                               icon:<Users size={14}/> },
                    { label:'Total',         value:`${totalAmount.toFixed(2)} ${form.currency}`,   icon:<DollarSign size={14}/> },
                    { label:'Platform Fee', value:'$0.00',                                          icon:<TrendingDown size={14}/>, purple:true },
                    { label:'Saved',         value:`~$${feeSaved}`,                                 icon:<CheckCircle size={14}/>, green:true },
                  ].map(it=>(
                    <div key={it.label} className={`bg-white rounded-lg p-3 border ${it.green?'border-green-200':'border-gray-200'}`}>
                      <div className={`flex items-center gap-1 text-xs mb-1 ${it.green?'text-green-600':it.purple?'text-purple-600':'text-gray-500'}`}>{it.icon}{it.label}</div>
                      <div className={`font-bold ${it.green?'text-green-600':it.purple?'text-purple-600':'text-gray-900'}`}>{it.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Schedule</p>
                <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700 border border-gray-200">
                  {form.payment_type==='one_time'
                    ? form.scheduled_at ? `⏰ Scheduled for ${new Date(form.scheduled_at).toLocaleString()}` : '⚡ Process immediately on confirm'
                    : `🔄 Recurring — ${recurrence.frequency.charAt(0).toUpperCase()+recurrence.frequency.slice(1)}, starting ${recurrence.start_date}`}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Funding</p>
                <div className={`flex items-center gap-3 p-4 rounded-xl border-2 ${
                  selectedFunding==='bank'?'border-green-400 bg-green-50':
                  selectedFunding==='card'?'border-blue-400 bg-blue-50':
                  selectedFunding==='crypto'?'border-orange-400 bg-orange-50':
                  'border-purple-400 bg-purple-50'}`}>
                  {selectedFunding==='bank'&&<Building size={20} className="text-green-600"/>}
                  {selectedFunding==='card'&&<CreditCard size={20} className="text-blue-600"/>}
                  {selectedFunding==='crypto'&&<Bitcoin size={20} className="text-orange-500"/>}
                  {selectedFunding==='wallet'&&<Wallet size={20} className="text-purple-600"/>}
                  <div>
                    <p className="font-semibold text-sm text-gray-900">
                      {selectedFunding==='wallet'?`Zanors Wallet — ${allWallets.find(w=>w.id===selectedWallet)?.name||''}`:
                       selectedFunding==='bank'?`Bank ****${bankAccounts.find(b=>b.id===selectedBankAccount)?.account_number_last4||''}`:
                       selectedFunding==='card'?'Credit / Debit Card (Stripe)':'Cryptocurrency Wallet'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{totalAmount.toFixed(2)} {form.currency} charged at processing</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recipients</p>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Name','Method','Amount','Account Info'].map(h=>
                          <th key={h} className="px-3 py-2.5 text-left text-xs text-gray-500 font-semibold">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((r,i)=>(
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-900">
                            <div className="font-medium">{r.recipient_name||'—'}</div>
                            <div className="text-xs text-gray-400">{r.recipient_email}</div>
                          </td>
                          <td className="px-3 py-2 text-xs capitalize text-gray-500">{(r.payment_method||'wallet').replace('_',' ')}</td>
                          <td className="px-3 py-2 font-semibold text-gray-900">{parseFloat(r.amount||0).toFixed(2)} {r.currency||form.currency}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {r.payment_method==='bank_domestic'&&`${r.bank_name||''} ****${(r.account_number||'').slice(-4)}`}
                            {r.payment_method==='bank_international'&&`${r.iban||''} ${r.swift_bic||''}`}
                            {r.payment_method==='crypto'&&<span className="font-mono">{(r.crypto_address||'').slice(0,16)}…</span>}
                            {r.payment_method==='wallet'&&r.zanors_email}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-2xl">
          <button type="button"
            onClick={()=>step>1?setStep(s=>s-1):onClose()}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
            {step>1?'← Back':'Cancel'}
          </button>
          {step<4 ? (
            <button type="button" onClick={()=>{setError('');setStep(s=>s+1);}} disabled={!canContinue}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 disabled:opacity-40 transition-colors flex items-center gap-2">
              Continue <ArrowRight size={15}/>
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={submitDisabled}
              className="px-6 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
              {loading&&<Loader size={14} className="animate-spin"/>}
              {form.scheduled_at?'Schedule Batch':form.payment_type==='recurring'?'Activate Schedule':'Create & Process'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Batch Detail Modal ─────────────────────────────────────────
function BatchDetailModal({ batchId, onClose, onProcess }) {
  const [data,setData]             = useState(null);
  const [loading,setLoading]       = useState(true);
  const [processing,setProcessing] = useState(false);
  const [tab,setTab]               = useState('recipients');

  useEffect(()=>{
    (async()=>{
      try {
        const { data:{ session } } = await supabase.auth.getSession();
        const res = await fetch(`${API}/batch-payments/${batchId}`,{ headers:{ Authorization:`Bearer ${session?.access_token}` } });
        setData(await res.json());
      } catch {}
      finally { setLoading(false); }
    })();
  },[batchId]);

  async function handleProcess() {
    setProcessing(true);
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      await fetch(`${API}/batch-payments/${batchId}/process`,{ method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}` } });
      onProcess(); onClose();
    } catch (err) { alert(err.message); }
    finally { setProcessing(false); }
  }

  if (loading) return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-10"><Loader size={32} className="animate-spin text-purple-600"/></div>
    </div>
  );
  const { batch, recipients=[], logs=[] } = data||{};
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{batch?.title}</h2>
              <StatusBadge status={batch?.status}/>
              {batch?.payment_type==='recurring'&&<span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">🔄 Recurring</span>}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{batch?.recipient_count} recipients · {parseFloat(batch?.total_amount||0).toFixed(2)} {batch?.currency}</p>
          </div>
          <button onClick={onClose}><X size={22} className="text-gray-400 hover:text-gray-600"/></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          {['recipients','logs'].map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${tab===t?'bg-purple-600 text-white':'text-gray-500 hover:bg-gray-100'}`}>{t}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {tab==='recipients'&&(
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Name','Amount','Method','Status','Ref'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs text-gray-500 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {recipients.map(r=>(
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-900"><div className="font-medium">{r.recipient_name}</div><div className="text-xs text-gray-400">{r.recipient_email}</div></td>
                    <td className="px-3 py-2 font-medium text-gray-900">{parseFloat(r.amount).toFixed(2)} {r.currency}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 capitalize">{(r.payment_method||'').replace('_',' ')}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status}/></td>
                    <td className="px-3 py-2 text-xs text-gray-400 font-mono">{r.transaction_ref||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab==='logs'&&(
            <div className="space-y-2">
              {logs.map(log=>(
                <div key={log.id} className="flex items-start gap-3 text-sm">
                  <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">{new Date(log.created_at).toLocaleString()}</span>
                  <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 whitespace-nowrap">{log.event}</span>
                  <span className="text-gray-700">{log.message}</span>
                </div>
              ))}
              {logs.length===0&&<p className="text-gray-400 text-sm text-center py-6">No events logged yet.</p>}
            </div>
          )}
        </div>
        {batch?.status==='draft'&&(
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button onClick={handleProcess} disabled={processing}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50">
              {processing?<Loader size={16} className="animate-spin"/>:<Play size={16}/>}
              Process Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page skeleton (shows instantly while loading) ───────────────
function PageSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-4">
        {/* Stats skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1,2,3,4].map(i=>(
            <div key={i} className="bg-white/10 rounded-xl p-4 animate-pulse">
              <div className="h-3 bg-white/20 rounded mb-3 w-2/3"/>
              <div className="h-7 bg-white/30 rounded w-1/2"/>
            </div>
          ))}
        </div>
        {/* Empty state instantly */}
        <div className="text-center py-20">
          <div className="text-7xl mb-4">📦</div>
          <h3 className="text-2xl font-bold text-white mb-2">Loading batches…</h3>
        </div>
      </div>
    </div>
  );
}

// ─── Inner page ─────────────────────────────────────────────────
function BatchPaymentsInner({ user }) {
  const [batches,       setBatches]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showCreate,    setShowCreate]    = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [loadError,     setLoadError]     = useState('');
  const abortRef = useRef(null);

  const loadBatches = useCallback(async () => {
    // Cancel previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setLoadError('');

    // Timeout after 8 s so we never spin forever
    const timeout = setTimeout(()=>controller.abort(), 8000);

    try {
      // Force refresh to avoid expired token 401s
      let { data:{ session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      // If token looks stale, refresh it
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) session = refreshed.session;

      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await fetch(`${API}/batch-payments${params}`, {
        signal: controller.signal,
        headers: { Authorization:`Bearer ${session.access_token}` }
      });
      clearTimeout(timeout);
      if (res.status === 401) throw new Error('AUTH_EXPIRED');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBatches(data.batches || []);
    } catch (err) {
      clearTimeout(timeout);
      if (err.name !== 'AbortError') {
        if (err.message === 'AUTH_EXPIRED') {
          setLoadError('Session expired — please refresh the page or log out and back in.');
        } else {
          setLoadError('Could not load batches — check your connection.');
        }
        setBatches([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(()=>{ loadBatches(); }, [loadBatches]);
  useEffect(()=>()=>abortRef.current?.abort(), []); // cleanup on unmount

  async function cancelBatch(id,e) {
    e.stopPropagation();
    if (!window.confirm('Cancel this batch?')) return;
    const { data:{ session } } = await supabase.auth.getSession();
    await fetch(`${API}/batch-payments/${id}/cancel`,{ method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}` } });
    loadBatches();
  }

  const stats = {
    total:     batches.length,
    completed: batches.filter(b=>b.status==='completed').length,
    volume:    batches.filter(b=>b.status==='completed').reduce((s,b)=>s+parseFloat(b.total_amount||0),0),
    saved:     batches.filter(b=>b.status==='completed').reduce((s,b)=>s+parseFloat(b.fee_saved||0),0),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">

      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-purple-300 hover:text-white text-sm transition-colors">← Dashboard</Link>
            <span className="text-purple-600">|</span>
            <h1 className="text-xl font-bold">Batch Payments</h1>
          </div>
          <button onClick={()=>setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-xl font-semibold text-sm transition-colors">
            <Plus size={16}/> New Batch
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Stats — always visible immediately */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label:'Total Batches',    value:loading?'—':stats.total,    icon:<FileText size={20}/>,     color:'from-purple-700 to-purple-800' },
            { label:'Completed',         value:loading?'—':stats.completed, icon:<CheckCircle size={20}/>, color:'from-green-700 to-green-800' },
            { label:'Volume Processed', value:loading?'—':`$${stats.volume.toLocaleString('en',{maximumFractionDigits:0})}`, icon:<DollarSign size={20}/>, color:'from-blue-700 to-blue-800' },
            { label:'Fees Saved',        value:loading?'—':`~$${stats.saved.toFixed(2)}`, icon:<TrendingDown size={20}/>, color:'from-yellow-700 to-yellow-800' },
          ].map(s=>(
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-xl p-4 border border-white/10`}>
              <div className="flex items-center gap-2 text-white/70 text-xs mb-2">{s.icon}{s.label}</div>
              <div className={`text-2xl font-bold ${loading?'animate-pulse text-white/40':'text-white'}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Value props */}
        <div className="bg-green-900/40 border border-green-500/40 rounded-xl p-4 mb-6 flex flex-wrap gap-3">
          {['✅ Automated payroll','✅ International contractors','✅ Recurring schedules','✅ CSV upload',
            '✅ Zanors Wallet · Plaid ACH · Stripe Card · Crypto','✅ Save up to 95% on fees'
          ].map(f=><span key={f} className="text-green-200 text-sm font-medium">{f}</span>)}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex gap-2 flex-wrap">
            {['','draft','pending','processing','completed','failed','cancelled'].map(s=>(
              <button key={s} onClick={()=>setFilterStatus(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors
                  ${filterStatus===s?'bg-purple-500 text-white':'bg-white/10 text-purple-200 hover:bg-white/20'}`}>
                {s||'All'}
              </button>
            ))}
          </div>
          <button onClick={loadBatches} disabled={loading} className="text-purple-300 hover:text-white transition-colors p-1 disabled:opacity-50">
            <RefreshCw size={16} className={loading?'animate-spin':''}/>
          </button>
        </div>

        {/* Error */}
        {loadError && (
          <div className="flex items-center gap-2 bg-red-500/20 border border-red-400/40 rounded-xl p-4 mb-4">
            <AlertCircle size={18} className="text-red-300 flex-shrink-0"/>
            <p className="text-red-200 text-sm">{loadError}</p>
            <button onClick={loadBatches} className="ml-auto text-red-300 hover:text-white text-xs underline">Retry</button>
          </div>
        )}

        {/* List */}
        {loading ? (
          /* Fast skeleton — appears in <100 ms */
          <div className="space-y-3">
            {[1,2,3].map(i=>(
              <div key={i} className="bg-white/10 rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex-shrink-0"/>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-white/20 rounded w-48"/>
                    <div className="h-3 bg-white/10 rounded w-32"/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : batches.length===0 ? (
          <div className="text-center py-20">
            <div className="text-7xl mb-4">📦</div>
            <h3 className="text-2xl font-bold mb-2">No batch payments yet</h3>
            <p className="text-purple-300 mb-6 max-w-sm mx-auto">Pay employees, contractors and vendors in bulk — save up to 95% on fees</p>
            <button onClick={()=>setShowCreate(true)}
              className="px-6 py-3 bg-green-600 hover:bg-green-500 rounded-xl font-semibold transition-colors">
              Create First Batch
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {batches.map(batch=>(
              <div key={batch.id} onClick={()=>setSelectedBatch(batch.id)}
                className="bg-white/10 border border-white/20 rounded-xl p-4 hover:bg-white/15 cursor-pointer transition-all hover:border-purple-400">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                      ${batch.status==='completed'?'bg-green-600':batch.status==='processing'?'bg-blue-600':batch.status==='failed'?'bg-red-600':'bg-purple-600'}`}>
                      {batch.status==='completed'?<CheckCircle size={20}/>:
                       batch.status==='processing'?<Loader size={20} className="animate-spin"/>:
                       batch.status==='failed'?<AlertCircle size={20}/>:
                       batch.payment_type==='recurring'?<RefreshCw size={20}/>:<FileText size={20}/>}
                    </div>
                    <div>
                      <div className="font-semibold flex items-center gap-2 flex-wrap">
                        {batch.title}<StatusBadge status={batch.status}/>
                        {batch.payment_type==='recurring'&&<span className="bg-blue-900/60 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">🔄 Recurring</span>}
                      </div>
                      <div className="text-sm text-purple-300">
                        {batch.recipient_count} recipients · {parseFloat(batch.total_amount||0).toFixed(2)} {batch.currency}
                        {batch.completed_count>0&&<span className="text-green-400 ml-2">✓ {batch.completed_count} paid</span>}
                        {batch.failed_count>0&&<span className="text-red-400 ml-2">✗ {batch.failed_count} failed</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {batch.next_run_at&&(
                      <div className="text-xs text-purple-300 text-right">
                        <div className="flex items-center gap-1 mb-0.5"><Calendar size={11}/> Next run</div>
                        <div>{new Date(batch.next_run_at).toLocaleDateString()}</div>
                      </div>
                    )}
                    <div className="text-xs text-white/40">{new Date(batch.created_at).toLocaleDateString()}</div>
                    {['draft','pending'].includes(batch.status)&&(
                      <button onClick={e=>cancelBatch(batch.id,e)} className="text-red-400 hover:text-red-300 p-1 transition-colors"><X size={16}/></button>
                    )}
                  </div>
                </div>
                {batch.status==='processing'&&batch.recipient_count>0&&(
                  <div className="mt-3">
                    <div className="bg-white/10 rounded-full h-1.5">
                      <div className="bg-blue-400 h-1.5 rounded-full animate-pulse" style={{width:`${Math.min(100,((batch.completed_count||0)/batch.recipient_count)*100)}%`}}/>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate&&<CreateBatchModal user={user} onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false);loadBatches();}}/>}
      {selectedBatch&&<BatchDetailModal batchId={selectedBatch} onClose={()=>setSelectedBatch(null)} onProcess={loadBatches}/>}
    </div>
  );
}

// ── DEFAULT EXPORT — wraps in <Elements> like Send.jsx ──────────
export default function BatchPaymentsPage({ user }) {
  return (
    <Elements stripe={stripePromise}>
      <BatchPaymentsInner user={user}/>
    </Elements>
  );
}